import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FiSun, FiMoon, FiCoffee } from 'react-icons/fi';
import { FaLeaf } from 'react-icons/fa';
import SafeIcon from '../common/SafeIcon';

const Dining = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const meals = [
    {
      icon: FiSun,
      title: 'Breakfast',
      time: '7:00 AM - 10:00 AM',
      description: 'Traditional Sri Lankan hoppers with fresh coconut sambol, tropical fruits, and Ceylon tea',
      image: 'https://images.unsplash.com/photo-1533089860892-a7c6f0a88666?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      highlights: ['Fresh coconut hoppers', 'Tropical fruit selection', 'Organic Ceylon tea', 'Local honey']
    },
    {
      icon: FiSun,
      title: 'Lunch',
      time: '12:30 PM - 2:30 PM',
      description: 'Authentic rice and curry with vegetables harvested from our organic garden',
      image: 'https://images.unsplash.com/photo-1565299624946-b28f40a0ca4b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      highlights: ['Garden-fresh vegetables', 'Traditional spice blends', 'Coconut-based curries', 'Herbal drinks']
    },
    {
      icon: FiMoon,
      title: 'Dinner',
      time: '7:00 PM - 9:00 PM',
      description: 'Candlelit dining featuring regional specialties and fresh river fish',
      image: 'https://images.unsplash.com/photo-1574484284002-952d92456975?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      highlights: ['Fresh river fish', 'Candlelit ambiance', 'Regional specialties', 'Vegetarian options']
    }
  ];

  const specialties = [
    { name: 'Sinharaja Fish Curry', description: 'Fresh river fish in coconut curry', spice: 'Medium' },
    { name: 'Wild Vegetable Mallung', description: 'Forest greens with coconut', spice: 'Mild' },
    { name: 'Pitadeniya Pol Sambol', description: 'Spicy coconut relish', spice: 'Hot' },
    { name: 'Rainforest Fruit Salad', description: 'Seasonal tropical fruits', spice: 'None' },
    { name: 'Mederipitiya Mushroom Curry', description: 'Local mushrooms in spices', spice: 'Medium' },
    { name: 'Kithul Treacle Dessert', description: 'Traditional palm syrup sweet', spice: 'None' }
  ];

  const farmToTable = [
    { item: 'Organic Vegetables', source: 'Our Garden', freshness: 'Same Day' },
    { item: 'River Fish', source: 'Gin Ganga River', freshness: 'Daily Catch' },
    { item: 'Coconuts', source: 'Property Trees', freshness: 'Fresh Daily' },
    { item: 'Spices', source: 'Local Farmers', freshness: 'Weekly' },
    { item: 'Fruits', source: 'Neighboring Orchards', freshness: 'Seasonal' }
  ];

  return (
    <section id="dining" className="py-20 relative overflow-hidden">
      <div className="container mx-auto px-6" ref={ref}>
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <motion.h2
            className="text-4xl md:text-5xl font-bold text-white mb-6 font-['Playfair_Display']"
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2 }}
          >
            Farm-to-Table <span className="text-gradient">Dining</span>
          </motion.h2>
          <motion.p
            className="text-xl text-gray-300 max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.4 }}
          >
            Savor authentic Sri Lankan flavors with ingredients harvested from our organic garden
          </motion.p>
        </motion.div>

        {/* Meal Times */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          {meals.map((meal, index) => (
            <motion.div
              key={meal.title}
              className="glass-card overflow-hidden hover-glow"
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.6 + index * 0.2 }}
              whileHover={{ scale: 1.02 }}
            >
              <div className="relative overflow-hidden">
                <motion.img
                  src={meal.image}
                  alt={meal.title}
                  className="w-full h-48 object-cover"
                  whileHover={{ scale: 1.1 }}
                  transition={{ duration: 0.6 }}
                />
                <div className="absolute top-4 left-4">
                  <div className="w-12 h-12 glass-morphism rounded-full flex items-center justify-center">
                    <SafeIcon icon={meal.icon} className="w-6 h-6 text-yellow-400" />
                  </div>
                </div>
              </div>
              
              <div className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xl font-semibold text-white font-['Playfair_Display']">
                    {meal.title}
                  </h3>
                  <span className="text-blue-300 text-sm font-medium">{meal.time}</span>
                </div>
                
                <p className="text-gray-300 mb-4 leading-relaxed">
                  {meal.description}
                </p>
                
                <div className="space-y-2">
                  {meal.highlights.map((highlight, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                      <span className="text-gray-300 text-sm">{highlight}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Specialties Menu */}
        <motion.div
          className="glass-card p-8 mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 1.2 }}
        >
          <h3 className="text-2xl font-bold text-white mb-6 text-center font-['Playfair_Display']">
            Signature Specialties
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {specialties.map((dish, index) => (
              <motion.div
                key={dish.name}
                className="glass-morphism p-4 rounded-lg hover-glow"
                initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ delay: 1.4 + index * 0.1 }}
                whileHover={{ scale: 1.02 }}
              >
                <div className="flex items-start justify-between mb-2">
                  <h4 className="text-lg font-semibold text-white">{dish.name}</h4>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    dish.spice === 'Hot' ? 'bg-red-500/20 text-red-300' :
                    dish.spice === 'Medium' ? 'bg-yellow-500/20 text-yellow-300' :
                    dish.spice === 'Mild' ? 'bg-green-500/20 text-green-300' :
                    'bg-blue-500/20 text-blue-300'
                  }`}>
                    {dish.spice}
                  </span>
                </div>
                <p className="text-gray-300 text-sm">{dish.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Farm to Table */}
        <motion.div
          className="glass-card p-8 mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 1.6 }}
        >
          <div className="text-center mb-8">
            <SafeIcon icon={FaLeaf} className="w-12 h-12 text-green-400 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-white mb-4 font-['Playfair_Display']">
              Our Farm-to-Table Promise
            </h3>
            <p className="text-gray-300 max-w-2xl mx-auto">
              Every ingredient tells a story of sustainability and freshness, 
              sourced within walking distance of your table.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {farmToTable.map((item, index) => (
              <motion.div
                key={item.item}
                className="glass-morphism p-4 rounded-lg text-center hover-glow"
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 1.8 + index * 0.1 }}
                whileHover={{ scale: 1.05 }}
              >
                <h4 className="text-white font-semibold mb-2">{item.item}</h4>
                <p className="text-gray-300 text-sm mb-1">Source: {item.source}</p>
                <p className="text-green-300 text-sm font-medium">{item.freshness}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Special Dietary Options */}
        <motion.div
          className="text-center"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 2.0 }}
        >
          <div className="glass-card p-6 max-w-2xl mx-auto">
            <SafeIcon icon={FiCoffee} className="w-8 h-8 text-orange-400 mx-auto mb-3" />
            <h4 className="text-xl font-semibold text-white mb-4 font-['Playfair_Display']">
              Dietary Preferences Welcome
            </h4>
            <p className="text-gray-300 mb-4">
              We cater to all dietary requirements including vegetarian, vegan, gluten-free, 
              and specific allergies. Please inform us during booking.
            </p>
            <div className="flex flex-wrap justify-center gap-3 text-sm">
              <span className="glass-morphism px-3 py-1 rounded-full text-white">Vegetarian</span>
              <span className="glass-morphism px-3 py-1 rounded-full text-white">Vegan</span>
              <span className="glass-morphism px-3 py-1 rounded-full text-white">Gluten-Free</span>
              <span className="glass-morphism px-3 py-1 rounded-full text-white">Halal</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Dining;